import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/lib/content";
import { scrollToId } from "@/lib/scroll";

export const Header = () => {
    const [open, setOpen] = useState(false);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 250 : 0);
    };

    return (
        <header className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[76px] flex items-center justify-between">
                <button
                    data-testid="nav-logo-button"
                    onClick={() => window.__lenis?.scrollTo(0)}
                    className="flex items-center gap-3"
                    aria-label="Brain Brush — back to top"
                >
                    <img src={LOGO_URL} alt="Brain Brush logo" className="h-10 w-10 rounded-lg shadow-md shadow-violet-500/20" />
                    <span className="font-display font-bold text-lg tracking-tight text-slate-900">Brain Brush</span>
                </button>

                <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.id}
                            data-testid={`nav-link-${link.id}`}
                            onClick={() => go(link.id)}
                            className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors duration-300"
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <button
                        data-testid="nav-cta-start-project"
                        onClick={() => go("contact")}
                        className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 shadow-lg shadow-violet-500/25 hover:shadow-pink-500/30 hover:scale-[1.03] transition-all duration-300"
                    >
                        Start a Project <ArrowRight size={15} />
                    </button>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="lg:hidden p-2 text-slate-800"
                    onClick={() => setOpen(!open)}
                    aria-label={open ? "Close menu" : "Open menu"}
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200/60"
                        aria-label="Mobile"
                    >
                        <div className="px-6 py-4 flex flex-col gap-1">
                            {NAV_LINKS.map((link) => (
                                <button
                                    key={link.id}
                                    data-testid={`nav-mobile-link-${link.id}`}
                                    onClick={() => go(link.id)}
                                    className="text-left py-3 text-base font-medium text-slate-700 hover:text-violet-600 transition-colors"
                                >
                                    {link.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-cta-start-project"
                                onClick={() => go("contact")}
                                className="gradient-bg text-white text-sm font-semibold px-5 py-3 rounded-full mt-2 inline-flex items-center justify-center gap-2"
                            >
                                Start a Project <ArrowRight size={15} />
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
